import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, List, Avatar } from 'antd';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PartyMember, Image } from '../../types/types';
import { useStableCallback } from '../../hooks/hookUtils';

interface WatchOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  partyRoster: PartyMember[];
  watchOrder: number[];
  imageLibrary: Image[];
  onUpdateWatchOrder: (newOrder: number[]) => void;
}

const SortablePlayerItem: React.FC<{ player: PartyMember; image: string | null; index: number }> = ({ player, image, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: player.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none', // Recommended for pointer sensor on touch devices
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <List.Item>
        <List.Item.Meta
          avatar={<Avatar src={image} style={{ backgroundColor: player.color ?? undefined }}>{player.name.charAt(0)}</Avatar>}
          title={`${index + 1}. ${player.name}`}
        />
      </List.Item>
    </div>
  );
};

const WatchOrderModal: React.FC<WatchOrderModalProps> = ({
  isOpen,
  onClose,
  partyRoster,
  watchOrder,
  imageLibrary,
  onUpdateWatchOrder,
}) => {
  const [orderedPlayers, setOrderedPlayers] = useState<PartyMember[]>([]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Memoize the image map to avoid recreating it on every render or every call
  // to getPlayerImage. This provides O(1) lookups instead of O(n) scans.
  const imageMap = useMemo(
    () => new Map((imageLibrary ?? []).map(i => [i.id, i])),
    [imageLibrary]
  );

  useEffect(() => {
    if (isOpen) {
      const playerMap = new Map(partyRoster.map(p => [p.id, p]));
      const sorted = watchOrder
        .map(playerId => playerMap.get(playerId))
        .filter((p): p is PartyMember => p !== undefined);

      // Add any players from the roster who aren't in the watch order yet
      partyRoster.forEach(player => {
        if (!watchOrder.includes(player.id as number)) {
          sorted.push(player);
        }
      });

      setOrderedPlayers(sorted);
    }
  }, [isOpen, partyRoster, watchOrder]);

  // Stable callback that uses the memoized imageMap. Since imageMap is stable
  // (only changes when imageLibrary changes), this function won't cause
  // unnecessary re-renders of child components.
  const getPlayerImage = useStableCallback((player: PartyMember) => {
    if (player.imageId) {
      const image = imageMap.get(player.imageId);
      if (image) return image.imageUrl;
    }
    return null;
  });

  // Stable callback for drag end handling to prevent unnecessary re-renders
  // of the DndContext and its children
  const handleDragEnd = useStableCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = (orderedPlayers ?? []).findIndex(p => p.id === active.id);
      const newIndex = (orderedPlayers ?? []).findIndex(p => p.id === over.id);
      const newOrder = arrayMove(orderedPlayers, oldIndex, newIndex);

      setOrderedPlayers(newOrder);
      onUpdateWatchOrder((newOrder ?? []).map(p => p.id as number));
    }
  });

  return (
    <Modal
      title="Set Camp Watch Order"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <p>Drag and drop to reorder the watch. The first person has the first watch.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={(orderedPlayers ?? []).map(p => p.id)} strategy={verticalListSortingStrategy}>
          <List
            dataSource={orderedPlayers}
            renderItem={(player, index) => (
              <SortablePlayerItem
                key={player.id}
                player={player}
                image={getPlayerImage(player)}
                index={index}
              />
          )}
        />
        </SortableContext>
      </DndContext>
    </Modal>
  );
};

export default WatchOrderModal;